import { white } from '@zocdoc/branding/colors';
import { ContentPrimitives, ControlPrimitives, ControlsAndContent } from '@zocdoc/components/src/AppointmentPicker';
import { breakpoint } from '@zocdoc/components/src/grid';
import { flowRight, pick } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router';
import sizeMe from 'react-sizeme';
import styled, { withTheme } from 'styled-components';
import addConstraintModals from '../../../Shared/Modals/addConstraintModals';
import { MARKETING_QUERY_PARAMS, TIMESGRID_QUERY_PARAMS } from '../../../constants';
import isZocdocBrand from '../../../utils/isZocdocBrand';
import { BookingSubSectionWithNoLabelOnMobile } from '../BookingSection/BookingSubSection';
import { LOCALIZED_STRINGS } from '../BookingSection/constants';
import addInsuranceToUrls from './addInsuranceToUrls';

export class BookingSectionTimesGridWrapperView extends React.PureComponent {
    static propTypes = {
        // used only by screenshot tests
        __skipTimesGridLoadingState: PropTypes.bool,

        ab: PropTypes.object,
        additionalBookingUrlParams: PropTypes.object,
        isExistingPatient: PropTypes.bool,
        isMobile: PropTypes.bool,

        /**
         * from react-router withRouter() HOC
         */
        location: PropTypes.shape({
            query: PropTypes.object,
        }),

        locationId: PropTypes.string,
        procedureId: PropTypes.string,
        provider: PropTypes.object,
        providerId: PropTypes.string,
        size: PropTypes.object,
        trackTimesgridClickMoreTimes: PropTypes.func.isRequired,
        trackTimesgridClickNext: PropTypes.func.isRequired,
        trackTimesgridClickNextAvailable: PropTypes.func.isRequired,
        trackTimesgridClickPrev: PropTypes.func.isRequired,
        trackTimesgridClickTime: PropTypes.func.isRequired,
    };

    static defaultProps = {
        __skipTimesGridLoadingState: false,
        isMobile: false,
        size: {},
    };

    constructor(props) {
        super(props);

        const {
            location,
            theme,
        } = props;

        const additionalBookingUrlParams = {
            directoryId: theme.directoryId,
        };

        if (location) {
            Object.assign(additionalBookingUrlParams, pick(location.query, MARKETING_QUERY_PARAMS));
            Object.assign(additionalBookingUrlParams, pick(location.query, TIMESGRID_QUERY_PARAMS));
        }

        this.state = {
            additionalBookingUrlParams,
            mounted: false,
        };
    }

    componentDidMount() {
        this.setState({
            mounted: true,
        });
    }

    render() {
        const {
            __skipTimesGridLoadingState,
            locationId,
            providerId,
            theme: { directoryId },
        } = this.props;

        if (!providerId || !locationId) {
            return (
                <div data-test="booking-section-timesgrid-empty"></div>
            );
        }

        if (isZocdocBrand(directoryId)) {
            return (
                <Wrapper ready={this.state.mounted || __skipTimesGridLoadingState}>
                    {this.renderTimesGrid()}
                </Wrapper>
            );
        } else {
            return (
                <BookingSubSectionWithNoLabelOnMobile
                    label={LOCALIZED_STRINGS.TimesGridLabel}
                >
                    <TimesGridStyleWrapper>
                        {this.renderTimesGrid()}
                    </TimesGridStyleWrapper>
                </BookingSubSectionWithNoLabelOnMobile>
            );
        }
    }

    renderTimesGrid() {
        const {
            __skipTimesGridLoadingState,
            insuranceCarrierId,
            insurancePlanId,
            isExistingPatient,
            isMobile,
            locationId,
            procedureId,
            provider,
            providerId,
            referrerType,
            size,
            theme,
            trackTimesgridClickMoreTimes,
            trackTimesgridClickNext,
            trackTimesgridClickNextAvailable,
            trackTimesgridClickPrev,
            trackTimesgridClickTime,
            trackTimesgridLoaded,
            widget,
        } = this.props;

        const isConfirmedMobile = this.forceMobileWhenComponentWidthIsTooSmall(size, isMobile);

        /**
         * there seems to be a bug in AP's handling of doctorLocationIds as an array,
         * possibly in a recent version of component SDK
         */
        return (
            <Timesgrid
                additionalBookingUrlParams={this.state.additionalBookingUrlParams}
                data-test="booking-section-timesgrid"
                directoryId={theme.directoryId}
                doctorLocationIds={[`${providerId}_${locationId}`]}
                handleClickEvent={false}
                insuranceCarrierId={insuranceCarrierId}
                insurancePlanId={insurancePlanId}
                isExistingPatient={isExistingPatient}
                isMobile={isConfirmedMobile}
                loading={!this.state.mounted || !__skipTimesGridLoadingState}
                onClickMore={trackTimesgridClickMoreTimes}
                onClickNext={trackTimesgridClickNext}
                onClickNextAvailable={trackTimesgridClickNextAvailable}
                onClickPrev={trackTimesgridClickPrev}
                onLoad={trackTimesgridLoaded}
                onlyCheckResourceConstraint
                onTimeSlotClicked={trackTimesgridClickTime}
                procedureId={procedureId || '-1'}
                provider={provider}
                referrerType={referrerType}
                searchProcedureId={procedureId}
                shouldJumpToFirstAppointment={isConfirmedMobile}
                updateParameters={() => {}}
                widget={widget}
            />
        );
    }

    forceMobileWhenComponentWidthIsTooSmall(componentWidth, isMobile) {
        if (componentWidth < 340) {
            return true;
        } else {
            return isMobile;
        }
    }
}

const Timesgrid = addConstraintModals(addInsuranceToUrls(ControlsAndContent));

const TimesGridStyleWrapper = styled.div`
    background-color: ${white};
    padding-bottom: 12px;

    ${ContentPrimitives.MobileWrapper} {
        ${breakpoint('medium', { down: true })} {
            padding-left: 12px;
        }
    }

    ${ContentPrimitives.MobileNoTimesView} {
        padding-right: 12px;
    }

    ${breakpoint.medium} {
        padding-top: 12px;
        padding-bottom: 24px;
    }
`;

const Wrapper = styled.div`
    background-color: ${white};
    padding-bottom: 12px;

    ${ContentPrimitives.DesktopWrapper},
    ${ContentPrimitives.MobileWrapper},
    ${ControlPrimitives.DesktopWrapper},
    ${ControlPrimitives.MobileWrapper} {
        opacity: ${p => p.ready ? 1 : 0};
        transition: opacity 300ms ease-in-out;
    }

    ${ContentPrimitives.MobileWrapper} {
        ${breakpoint('medium', { down: true })} {
            padding-left: 12px;
        }
    }

    ${ContentPrimitives.MobileNoTimesView} {
        padding-right: 12px;
    }

    ${breakpoint.medium} {
        padding-top: 12px;
        padding-bottom: 24px;
    }
`;

export default flowRight(
    withTheme,
    withRouter,
    sizeMe(),
)(BookingSectionTimesGridWrapperView);
